/**
 * Supabase Error Handling Utilities
 *
 * Handles PostgREST errors gracefully, especially PGRST205 (Schema Cache Miss)
 * which occurs when PostgREST's schema cache is out of sync with the database.
 *
 * @see https://postgrest.org/en/stable/errors.html
 */

// ============================================
// TYPES
// ============================================

export interface SupabaseError {
  code: string;
  message: string;
  details: string | null;
  hint: string | null;
}

export interface RetryConfig {
  maxRetries: number;
  delayMs: number;
  retryableCodes: string[];
}

// ============================================
// CUSTOM ERROR CLASSES
// ============================================

/**
 * Error thrown when the system is updating (e.g., schema cache refresh)
 * UI should show a friendly message and suggest retry after delay
 */
export class SystemUpdatingError extends Error {
  code: string;
  retryAfterMs: number;

  constructor(message: string, retryAfterMs = 10000) {
    super(message);
    this.name = 'SystemUpdatingError';
    this.code = 'SYSTEM_UPDATING';
    this.retryAfterMs = retryAfterMs;
  }
}

// ============================================
// CONSTANTS
// ============================================

export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 1,
  delayMs: 2000,
  retryableCodes: ['PGRST205'],
};

// PostgREST error codes that indicate schema cache issues
const SCHEMA_CACHE_ERROR_CODES = ['PGRST205'];

// ============================================
// ERROR DETECTION
// ============================================

/**
 * Check if error is a schema cache miss (PGRST205)
 */
export function isSchemasCacheError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as { code?: string };
  return SCHEMA_CACHE_ERROR_CODES.includes(err.code || '');
}

/**
 * Check if a Supabase error is retryable
 */
export function isRetryableError(error: unknown, config: RetryConfig = DEFAULT_RETRY_CONFIG): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as { code?: string };
  return config.retryableCodes.includes(err.code || '');
}

// ============================================
// ERROR MESSAGES
// ============================================

/**
 * Get user-friendly message for Supabase errors
 */
export function getSupabaseErrorMessage(error: SupabaseError | unknown): string {
  if (!error || typeof error !== 'object') {
    return 'Error desconocido. Por favor intenta de nuevo.';
  }

  const err = error as SupabaseError;

  // Schema cache error - system updating
  if (err.code === 'PGRST205') {
    return 'Actualizando configuración de seguridad. Por favor reintenta en 10 segundos.';
  }

  // Authentication errors
  if (err.code === '401' || err.code === 'PGRST301') {
    return 'No autorizado. Por favor inicia sesión nuevamente.';
  }

  // Permission errors
  if (err.code === '403' || err.code === 'PGRST302') {
    return 'No tienes permiso para realizar esta acción.';
  }

  // Not found
  if (err.code === '404' || err.code === 'PGRST116') {
    return 'El recurso solicitado no existe.';
  }

  // Rate limiting
  if (err.code === '429') {
    return 'Demasiadas solicitudes. Por favor espera un momento.';
  }

  // Server errors
  if (err.code?.startsWith('5')) {
    return 'Error del servidor. Por favor intenta más tarde.';
  }

  // Default: include the original message for debugging
  return err.message ? `Error: ${err.message}` : 'Error desconocido. Por favor intenta de nuevo.';
}

// ============================================
// RETRY LOGIC
// ============================================

/**
 * Execute an async operation with retry logic for schema cache errors
 *
 * @param operation - The async operation to execute
 * @param config - Retry configuration
 * @returns The result of the operation
 * @throws SystemUpdatingError if max retries exceeded for schema cache errors
 * @throws The original error for non-retryable errors
 *
 * @example
 * ```ts
 * const result = await withSchemaRetry(async () => {
 *   const { data, error } = await supabase.from('tenants').update(flags);
 *   if (error) throw error;
 *   return data;
 * });
 * ```
 */
export async function withSchemaRetry<T>(
  operation: () => Promise<T>,
  config: RetryConfig = DEFAULT_RETRY_CONFIG
): Promise<T> {
  let lastError: unknown;

  for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;

      const err = error as { code?: string };
      if (config.retryableCodes.includes(err?.code || '')) {
        if (attempt < config.maxRetries) {
          // Wait before retry
          await new Promise(resolve => setTimeout(resolve, config.delayMs));
          continue;
        }
        // Max retries exceeded for schema cache error
        throw new SystemUpdatingError(
          'Actualizando configuración de seguridad. Por favor reintenta en 10 segundos.',
          10000
        );
      }
      // Non-retryable error, throw immediately
      throw error;
    }
  }

  throw lastError;
}

/**
 * Wrap a Supabase operation with error handling
 * Returns a tuple of [data, error] where error is a user-friendly message
 *
 * @example
 * ```ts
 * const [data, errorMessage] = await safeSupabaseOp(async () => {
 *   const { data, error } = await supabase.from('tenants').select();
 *   if (error) throw error;
 *   return data;
 * });
 *
 * if (errorMessage) {
 *   showToast(errorMessage, 'error');
 *   return;
 * }
 * ```
 */
export async function safeSupabaseOp<T>(
  operation: () => Promise<T>,
  options?: { retry?: boolean }
): Promise<[T | null, string | null]> {
  try {
    const executor = options?.retry !== false ? withSchemaRetry : operation;
    const result = await (options?.retry !== false ? withSchemaRetry(operation) : operation());
    return [result, null];
  } catch (error) {
    const message = getSupabaseErrorMessage(error);
    return [null, message];
  }
}
